import { useSelector } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';

import { Grid, Typography } from '@mui/material';

import ExcludedFilters from './ExcludedFilters';
import { snackAlertData } from 'utils/constants';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useCompanyService } from 'store/services/company.service';
import { getBankAccountsDropdown } from 'store/services/bank.Service';
import { useInventoryService } from 'store/services/inventory.service';
import { parseExcludedFilters } from 'components/Inventory/utils/parseFilters';
import { excludedTableHeaders } from 'components/Inventory/utils/excludedTableHeaders';
import { DataTableWithAccordion, CircleLoading, SnackBarAlert, TablePagination } from 'shared';

const Excluded = () => {
    const { getCompanyUsersDropdown } = useCompanyService();
    const filters = useSelector((state) => state.inventoryFilter);
    const companyData = useSelector((state) => state.company.data);
    const isMobileScreen = useCustomMediaQuery('(max-width: 600px)');
    const { getExcludedPlaidTransactions, undoExcludedPlaidTransaction } = useInventoryService();

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [users, setUsers] = useState(null);
    const [banks, setBanks] = useState(null);
    const [selected, setSelected] = useState([]);
    const [loadingRowId, setLoadingRowId] = useState(null);
    const [tableLoading, setTableLoading] = useState(true);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [revertAllLoading, setRevertAllLoading] = useState(false);
    const [filterValues, setFilterValues] = useState(parseExcludedFilters(filters));

    const handleGetCompanyUsersDropdown = async () => {
        try {
            const response = await getCompanyUsersDropdown(companyData?.id);
            const userValues = response.data.map((user) => {
                return { label: user.name, value: user.id };
            });
            setUsers([{ value: 'all', label: 'All users' }, ...userValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetExcludedPlaidTransactions = async (page, filters = {}) => {
        try {
            setTableLoading(true);

            const response = await getExcludedPlaidTransactions({
                companyId: companyData.id,
                page,
                ...filters,
            });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleGetBankAccountsDropdown = async () => {
        try {
            const response = await getBankAccountsDropdown(companyData?.id);
            const bankValues = response.data.map((bank) => {
                return { label: bank.name, value: bank.id };
            });
            setBanks([{ value: 'all', label: 'All accounts' }, ...bankValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleUndoExcludedPlaidTransaction = async (rowData) => {
        try {
            setLoadingRowId(rowData?.id);
            const response = await undoExcludedPlaidTransaction({
                company_id: companyData?.id,
                plaid_transaction_ids: [rowData?.id],
            });
            setSelected((prevSelected) => prevSelected.filter((id) => id !== rowData.id));
            handleSuccessAlert(setAlertData, response.message);

            setData((prevData) => {
                if (!prevData) return null;

                const newResults = prevData.results.filter((item) => item.id !== rowData.id);
                const newCount = prevData.count - 1;
                const isFirstPage = page === 1;
                const shouldGoToPrevPage = newResults.length === 0 && !isFirstPage;

                if (newResults.length === 0 && isFirstPage) {
                    handleGetExcludedPlaidTransactions(1, filterValues);
                } else if (shouldGoToPrevPage) {
                    const newPage = page - 1;
                    setPage(newPage);
                    handleGetExcludedPlaidTransactions(newPage, filterValues);
                }

                return {
                    ...prevData,
                    count: newCount,
                    results: newResults,
                };
            });
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoadingRowId(null);
        }
    };

    const handlePageChange = async (page) => {
        setPage(page);
        setSelected([]);
        await handleGetExcludedPlaidTransactions(page, filterValues);
    };

    const handleDownloadExcludedCsv = async (data) => {
        const headers = excludedTableHeaders(() => {}, null);

        const csvHeaders = headers.map((col) => col.label);
        const csvRows = [csvHeaders.join(',')];

        data.forEach((item) => {
            const rowValues = headers.map((col) => {
                switch (col.id) {
                    case 'excluded_by':
                        return `${
                            item?.email
                                ? `${item.first_name} ${item.last_name}`
                                : item?.excluded_by_rule
                                  ? 'Excluded by rule'
                                  : '-'
                        }`;

                    default:
                        return item[col.id] ?? '';
                }
            });

            const csvLine = rowValues.map((val) => `"${val}"`).join(',');

            csvRows.push(csvLine);
        });

        const csvString = csvRows.join('\n');

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `excluded_transaction_${new Date()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadExcluded = async () => {
        try {
            setDownloadLoading(true);
            const { bankAccount, type, description, ...restFilters } = filterValues;

            const params = {
                companyId: companyData?.id,
                ...restFilters,
            };

            if (bankAccount?.value && bankAccount.value !== 'all') {
                params.bank_account_id = bankAccount.value;
            }

            if (type?.value) {
                params.type = type.value;
            }

            if (description) {
                params.search = description;
            }

            const response = await getExcludedPlaidTransactions(params);
            handleDownloadExcludedCsv(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleFiltersSubmit = async (values) => {
        try {
            setPage(1);
            setSelected([]);
            setFilterValues(values);
            handleGetExcludedPlaidTransactions(1, values);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            const selectedRows = [];
            data?.results?.forEach((cellData) => {
                if (!selected.includes(cellData.id)) {
                    selectedRows.push(cellData.id);
                }
            });
            setSelected([...selected, ...selectedRows]);
        } else {
            setSelected([]);
        }
    };

    const handleSelectRow = (event, id) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter((item) => item !== id));
        }
    };

    const handleRevertAll = async () => {
        try {
            setRevertAllLoading(true);
            const response = await undoExcludedPlaidTransaction({
                company_id: companyData?.id,
                plaid_transaction_ids: selected,
            });
            setSelected([]);
            handleSuccessAlert(setAlertData, response.message);
            const pageNumber =
                data?.results?.length == selected.length && page != 1 ? page - 1 : page;
            setPage(pageNumber);
            handleGetExcludedPlaidTransactions(pageNumber, filterValues);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setRevertAllLoading(false);
        }
    };

    useEffect(() => {
        if (companyData) {
            handleGetExcludedPlaidTransactions(page, filterValues);
            handleGetBankAccountsDropdown();
            handleGetCompanyUsersDropdown();
        }
    }, [companyData]);

    return (
        <Fragment>
            {companyData?.id && data && banks ? (
                <Grid container>
                    <Grid item xs={12}>
                        <ExcludedFilters
                            users={users || []}
                            banks={banks || []}
                            selected={selected}
                            downloadLoading={downloadLoading}
                            handleRevertAll={handleRevertAll}
                            revertAllLoading={revertAllLoading}
                            handleFiltersSubmit={handleFiltersSubmit}
                            handleDownloadExcluded={handleDownloadExcluded}
                        />
                    </Grid>
                    {data?.count > 0 && (
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            marginBottom="5px"
                            justifyContent="flex-end"
                        >
                            <Typography fontSize="18px">{`TOTAL: ${data?.count} ROW${
                                data?.count > 1 ? 'S' : ''
                            }`}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {/* <DataTable
                            headCells={excludedTableHeaders(
                                (rowData) => handleUndoExcludedPlaidTransaction(rowData),
                                loadingRowId,
                            )}
                            isLoading={tableLoading}
                            data={data.results}
                        /> */}
                        <DataTableWithAccordion
                            data={data}
                            selected={selected}
                            isSelectable={true}
                            isLoading={tableLoading}
                            setSelected={setSelected}
                            showHeaderCheckBox={true}
                            handleSelectRow={handleSelectRow}
                            handleSelectAllRows={handleSelectAllRows}
                            headers={excludedTableHeaders(
                                (rowData) => handleUndoExcludedPlaidTransaction(rowData),
                                loadingRowId,
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            isMobileScreen={isMobileScreen}
                            page={page}
                            count={data?.count}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircleLoading />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default Excluded;
